import { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import { QUIZ_EVENT_TYPES } from 'src/types/Quiz';
import { Input } from '@get-mosh/msh-shared';
import { useJsApiLoader, Libraries } from '@react-google-maps/api';
import { useAddressValidHDS } from 'src/hooks/useAddressValidHDS';
import { Vertical } from 'src/constants/Verticals';
import { env } from 'src/env.mjs';
import { isCountry } from 'src/constants/Countries';

const INVALID_HDS_WARNING_TEXT =
    'Our delivery partner may be unable to deliver refrigerated items to your suburb';

const LIBRARIES: Libraries = ['places'];

const QuizSuburb = () => {
    const { isLoaded: googleMapsLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: env.NEXT_PUBLIC_GOOGLE_MAPS_KEY,
        libraries: LIBRARIES,
    });

    const { quizToken, quizType, currentQuestion, handleSubmitAnswer, state } =
        useContext(QuizContext);
    const vertical = useMemo(
        () => Vertical[quizType.toUpperCase()],
        [quizType],
    );
    const {
        answerList,
        questionImage,
        questionText,
        questionExplanation,
        additionalText,
    } = currentQuestion || {};
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;
    const [addressValid, setAddressValid] = useState(false);
    const [suburb, setSuburb] = useState('');
    const [postcode, setPostcode] = useState('');
    const [stateName, setStateName] = useState('');
    const [error, setError] = useState('');
    const { data, isLoading: processingValidHDS } = useAddressValidHDS({
        suburb,
        postcode,
        quizToken: quizToken || '',
        disabled: vertical !== Vertical.WEIGHT_LOSS,
    });
    const { is_serviceable: isValidHDS } = data || {};

    const errorMessage = isCountry('NZ')
        ? 'Please provide a valid postcode'
        : 'Please type your suburb and postcode then choose an option';

    // Handle submitting
    const handleContinue = useCallback(() => {
        if (currentQuestion.questionId && !continueDisabled) {
            const { answerId: suburbAnswerId = '' } = answerList[0] || {};
            const { answerId: postcodeAnswerId = '' } = answerList[1] || {};
            const { answerId: stateAnswerId = '' } = answerList[2] || {};
            handleSubmitAnswer({
                questionId: currentQuestion.questionId,
                questionText: currentQuestion.questionText,
                answerIds: [suburbAnswerId, postcodeAnswerId, stateAnswerId],
                answerTexts: [suburb, postcode, stateName],
            });
        }
    }, [
        currentQuestion.questionId,
        currentQuestion.questionText,
        continueDisabled,
        answerList,
        handleSubmitAnswer,
        suburb,
        postcode,
        stateName,
    ]);

    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && addressValid) handleContinue();
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () => {
            window.removeEventListener('keydown', handleKeyPress, false);
        };
    }, [handleContinue, addressValid]);

    // Continue click listener
    useEffect(() => {
        window.addEventListener(QUIZ_EVENT_TYPES.continueClick, handleContinue);

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleContinue,
            );
    }, [handleContinue]);

    const validate = useCallback(
        (suburb: string, postcode: string) => {
            const isValid = isCountry('NZ')
                ? !!postcode
                : !!suburb && !!postcode;
            setAddressValid(isValid);
            setError(!isValid ? errorMessage : '');

            setContinueDisabled(!isValid);
        },
        [setContinueDisabled, errorMessage],
    );

    useEffect(() => {
        if (!googleMapsLoaded) return;

        const input = document.getElementById(
            'autocomplete',
        ) as HTMLInputElement;
        const autocomplete = new google.maps.places.Autocomplete(input, {
            types: ['(regions)'],
            fields: ['address_component'],
            componentRestrictions: { country: env.NEXT_PUBLIC_COUNTRY_CODE },
        });
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.preventDefault();
            }
        };

        input.addEventListener('keydown', onKeyDown);

        autocomplete.setFields(['address_component']);

        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();

            const suburb =
                place.address_components?.find((component) =>
                    component.types.includes('locality'),
                )?.long_name ?? '';
            const postcode =
                place.address_components?.find((component) =>
                    component.types.includes('postal_code'),
                )?.long_name ?? '';
            const stateName =
                place.address_components?.find((component) =>
                    component.types.includes('administrative_area_level_1'),
                )?.short_name ?? '';

            setSuburb(suburb);
            setPostcode(postcode);
            setStateName(stateName);
            validate(suburb, postcode);
        });

        return () => {
            google.maps.event.clearInstanceListeners(autocomplete);
        };
    }, [googleMapsLoaded, validate]);

    return (
        <>
            <div className="flex w-full flex-col gap-6">
                <picture>
                    <img
                        src={questionImage}
                        className="h-16 w-16"
                        alt={questionText}
                    />
                </picture>
                <div>
                    <h4>{questionText}</h4>
                    {questionExplanation ? (
                        <h5 className="highlight text-grey-400">
                            {questionExplanation}
                        </h5>
                    ) : null}
                </div>
                <Input
                    data-testid="google-suburb-input"
                    id="autocomplete"
                    name="suburb"
                    placeholder={additionalText}
                    onBlur={() => validate(suburb, postcode)}
                    error={error}
                    isLoading={!!processingValidHDS}
                />
            </div>
            {isValidHDS === false ? (
                <div className="p3 bg-yellow-warning text-orange-dark -mt-2 rounded-lg p-2">
                    {INVALID_HDS_WARNING_TEXT}
                </div>
            ) : null}
        </>
    );
};

export default QuizSuburb;
